import { useEffect, useMemo, useRef, useState } from 'react'
import { isSupported, playSteps, stop } from '@/audio/player'
import { binnenDauerS, binnenTimeline } from '@/core/binnenSignale'
import { binnenSignale } from '@/core/data'
import type { Key } from '@/core/i18n'
import type { BinnenSignalData } from '@/core/schema'
import { binschstro } from '@/core/sources'
import { whistle } from '@/core/whistle'
import { MitBegriffen } from './MitBegriffen'
import { useSettings } from './settings'
import { Viewer } from './Viewer'

/**
 * Wer das Zeichen gibt, entscheidet über das Licht. Ein Maschinenfahrzeug gibt zu jedem
 * Schallzeichen ein gelbes Lichtzeichen gleicher Dauer (§ 4.01 Nummer 2), ein
 * Kleinfahrzeug nicht, und es gibt ohnehin nur die Allgemeinen Zeichen (§ 4.02 Nummer 2).
 * Die beiden Lichterbilder sind die der Ordnung für diese Fahrzeuge.
 */
const FAHRZEUGE = {
  maschine: 'b308-maschine',
  klein: 'b313-kleinfahrzeug-maschine',
} as const
type Fahrzeug = keyof typeof FAHRZEUGE

function stepKey(kind: string): Key {
  return `signals.step.${kind}` as Key
}

/**
 * Die Schallzeichen der Binnenschifffahrt, Anlage 6 der Ordnung.
 *
 * Auf dem Rhein heißt derselbe Ton etwas anderes als auf See. Ein kurzer Ton meldet dort
 * einen Kurswechsel nach Steuerbord, bei der Begegnung aber verlangt er die Vorbeifahrt
 * an Backbord, und er ist dann eine Frage an ein bestimmtes Fahrzeug, die eine Antwort
 * verlangt. Deshalb zeigt dieser Bildschirm die Zeichen als das, was sie sind: als
 * Dialog. Zu jeder Anfrage stehen ihre Antworten, zu jeder Antwort, worauf sie antwortet,
 * und beim Überholen steht das Schweigen als Zustimmung dabei.
 */
export function BinnenSignale() {
  const { t, settings } = useSettings()
  const [id, setId] = useState(binnenSignale[0]?.id ?? '')
  const [spielt, setSpielt] = useState<string | undefined>()
  const [fahrzeug, setFahrzeug] = useState<Fahrzeug>('maschine')
  const [licht, setLicht] = useState<{ schritte: ReturnType<typeof binnenTimeline>; kennung: number }>()
  const aktiv = useRef(true)

  const zeichen = useMemo(() => binnenSignale.find((z) => z.id === id) ?? binnenSignale[0]!, [id])
  const schritte = useMemo(() => binnenTimeline(zeichen), [zeichen])
  const antworten = binnenSignale.filter((z) => z.antwortAuf === zeichen.id)
  const frage = zeichen.antwortAuf ? binnenSignale.find((z) => z.id === zeichen.antwortAuf) : undefined

  useEffect(() => {
    aktiv.current = true
    return () => {
      aktiv.current = false
      stop()
    }
  }, [])

  async function spiele(ziel: BinnenSignalData): Promise<void> {
    if (spielt === ziel.id) {
      stop()
      setSpielt(undefined)
      return
    }
    setSpielt(ziel.id)
    const schritte = binnenTimeline(ziel)
    // Das Licht blitzt nur, wenn die Ordnung es verlangt: Maschinenfahrzeug, und kein
    // Glockenzeichen.
    const mitLicht = ziel.lichtzeichen && fahrzeug === 'maschine'
    setLicht(mitLicht ? { schritte, kennung: Date.now() } : undefined)
    try {
      await playSteps(schritte, whistle('mittel'))
    } finally {
      if (aktiv.current) {
        setSpielt((jetzt) => (jetzt === ziel.id ? undefined : jetzt))
        setLicht(undefined)
      }
    }
  }

  const wiederholung = (z: BinnenSignalData): string => {
    if (z.repeatS <= 0) return t('signals.repeat.once')
    if (z.repeatS >= 60) return t('binnenSignale.minute')
    return t('binnenSignale.wiederholt')
  }

  const eintrag = (z: BinnenSignalData) => (
    <button
      key={z.id}
      type="button"
      className={`states__item${z.id === zeichen.id ? ' states__item--on' : ''}`}
      data-signal={z.id}
      data-testid={`binnen-signal-${z.id}`}
      onClick={() => setId(z.id)}
    >
      <span className="states__rule">{z.pattern.length === 0 ? t('binnenSignale.schweigen.kurz') : z.name.de}</span>
      <span lang="de">{z.bedeutung}</span>
    </button>
  )

  return (
    <section className="signals" data-testid="binnen-signale">
      {binschstro.anlage6.abschnitte
        .filter((a) => binnenSignale.some((z) => z.abschnitt === a.buchstabe))
        .map((abschnitt) => (
          <div className="catalogue__group" key={abschnitt.buchstabe} data-abschnitt={abschnitt.buchstabe}>
            <p className="catalogue__rule" lang="de">
              {abschnitt.buchstabe}. {abschnitt.titel}
            </p>
            <div className="catalogue__items">
              {binnenSignale.filter((z) => z.abschnitt === abschnitt.buchstabe).map(eintrag)}
            </div>
          </div>
        ))}

      <article className="detail" data-testid="signal-detail" data-signal={zeichen.id}>
        <h2 lang="de">{zeichen.pattern.length === 0 ? t('binnenSignale.schweigen.kurz') : zeichen.name.de}</h2>
        <p className="detail__text" lang="de" data-testid="binnen-bedeutung">
          {zeichen.bedeutung}
        </p>
        {zeichen.wer && (
          <p className="signal__legend" data-testid="binnen-wer">
            {t(`binnenSignale.wer.${zeichen.wer}` as Key)}
          </p>
        )}

        {/* Das Bild zum Ton: auf dem Rhein sieht man ein Schallzeichen auch, als gelbes
            Licht im Takt der Töne. */}
        <Viewer
          subject={{ kind: 'vessel', id: FAHRZEUGE[fahrzeug] }}
          mode={settings.mode}
          lichtzeichen={licht}
        />
        <div className="modes" role="group" data-testid="binnen-fahrzeug">
          {(Object.keys(FAHRZEUGE) as Fahrzeug[]).map((f) => (
            <button
              key={f}
              type="button"
              className={`modes__item${fahrzeug === f ? ' modes__item--on' : ''}`}
              data-fahrzeug={f}
              aria-pressed={fahrzeug === f}
              onClick={() => {
                stop()
                setLicht(undefined)
                setFahrzeug(f)
              }}
            >
              {t(`binnenSignale.fahrzeug.${f}` as Key)}
            </button>
          ))}
        </div>

        {zeichen.pattern.length > 0 ? (
          <>
            <div className="signal__bar" data-testid="signal-timeline" data-signal={zeichen.id}>
              {schritte.map((schritt, index) => (
                <span
                  key={`${schritt.kind}-${index}`}
                  className={`signal__step signal__step--${schritt.kind}`}
                  data-kind={schritt.kind}
                  style={{ flexGrow: schritt.duration }}
                  title={`${t(stepKey(schritt.kind))}, ${schritt.duration.toFixed(2)} s`}
                />
              ))}
            </div>
            <p className="signal__legend" data-testid="signal-length">
              {t('signals.length', {
                steps: schritte
                  .filter((s) => s.kind !== 'silence')
                  .map((s) => t(stepKey(s.kind)))
                  .join(' · '),
                seconds: binnenDauerS(zeichen).toFixed(binnenDauerS(zeichen) % 1 ? 2 : 0),
              })}
            </p>
            <div className="signal__controls">
              <button
                type="button"
                className="signal__play"
                data-testid="signal-play"
                disabled={!isSupported()}
                onClick={() => void spiele(zeichen)}
              >
                {spielt === zeichen.id ? t('signals.stop') : t('signals.play')}
              </button>
            </div>
          </>
        ) : (
          <p className="detail__text" data-testid="binnen-schweigen">
            {t('binnenSignale.schweigen')}
          </p>
        )}

        {/* Der Dialog: wer fragt, wer antwortet, und was auf eine Antwort folgt. */}
        {frage && (
          <p className="detail__text" data-testid="binnen-antwort-auf">
            {t('binnenSignale.antwortAuf')}{' '}
            <button type="button" className="toggle" onClick={() => setId(frage.id)} lang="de">
              {frage.bedeutung}
            </button>
          </p>
        )}
        {antworten.length > 0 && (
          <div data-testid="binnen-antworten">
            <h3 className="detail__legend">{t('binnenSignale.antworten')}</h3>
            <div className="catalogue__items">{antworten.map(eintrag)}</div>
          </div>
        )}

        <MitBegriffen text={zeichen.erklaerung} className="detail__text" lang="de" />
        <p className="detail__text" data-testid="signal-repeat">
          {wiederholung(zeichen)}
        </p>
        <p className="signal__legend" data-testid="binnen-licht">
          {t(zeichen.lichtzeichen ? 'binnenSignale.licht' : zeichen.pattern.length === 0 ? 'binnenSignale.licht.schweigen' : 'binnenSignale.licht.glocke')}
        </p>
        <p className="signal__legend" data-testid="binnen-kleinfahrzeug">
          {t(zeichen.kleinfahrzeug ? 'binnenSignale.kleinfahrzeug.ja' : 'binnenSignale.kleinfahrzeug.nein')}
        </p>

        <h3 className="detail__legend" data-testid="signal-citation" lang="de">
          BinSchStrO {zeichen.fundstelle}
        </h3>
        <p className="detail__disclaimer">{t('detail.disclaimer.binnen')}</p>
      </article>
    </section>
  )
}
